import { later } from '@ember/runloop';
import { Promise } from 'rsvp';
import { buildWaiter } from '@ember/test-waiters';
import $ from 'jquery';
import { extend } from '@square/dashboard-utilities/utils/keys';
import { escapeString } from '@square/dashboard-utilities/utils/xss';
import { getCookie } from '@square/dashboard-utilities/utils/cookies';
import { AuthzEngines, AuthzEnginesManager } from '@square/authz-engines';
const $ajax = $.ajax;
const testWaiter = buildWaiter('@square/dashboard-utilities:utils/xhr');

/**
 * Extract the response error from the server response.
 *
 * This had to be handled differently for ember model and ember data.
 *
 * response.responseText is handling ember model errors.
 * response.errors... is handling ember data errors.
 * @param {*} response the text defining the error that occurred.
 */
export function getResponseMessage(response) {
  if (!response.errors) {
    return JSON.parse(response.responseText).message || JSON.parse(response.responseText).error;
  } else {
    return response.errors.firstObject?.attributes?.message || response.errors.firstObject?.attributes?.title;
  }
}

/**
 * Handle if the response error was due to a session timeout.
 *
 * 'response?.status' handles 401s for ember model
 * 'response?.errors?.[0]?.status' handles 401s for ember data
 *
 * TODO: evaluate if the handler for ember model can be removed once ember model is removed.
 */
export function isUserSessionTimeout(response) {
  return response?.status === 401 || response?.errors?.[0]?.status === '401';
}
function makeRequest(url, options) {
  // Register an internal waiter when in testing, so we can wait for the request to be done
  const testWaiterToken = testWaiter.beginAsync();
  return new Promise((resolve, reject) => {
    // eslint-disable-next-line ember/no-jquery
    $ajax(url, options).then(resolve, xhr => {
      if (xhr.getResponseHeader(AuthzEngines.ENVOY_SAFE_AUTH_HEADER) === AuthzEngines.ENVOY_SAFE_AUTH_DECISION) {
        const obligation = xhr.responseJSON.errors[0]?.obligations[0];
        if (obligation) {
          resolve(AuthzEnginesManager.populateUI({
            obligation
          }).then(() => makeRequest(url, options)).catch(reject));
          return;
        }
      }
      // Attach the request URL to improve automatic Sentry exception reporting
      reject({
        ...xhr,
        url
      }); // eslint-disable-line prefer-promise-reject-errors
    });
  }).finally(() => {
    testWaiter.endAsync(testWaiterToken);
  });
}
function shouldRetry(xhr) {
  if (!xhr) {
    return false;
  }
  return xhr.statusText === 'timeout' || xhr.status >= 500 && xhr.status < 600;
}
export const xhrMocks = {
  ajax(url, options = {}) {
    // FIXME(mattwright,donovan): `$.ajax()` supports a single argument format
    // where just an options/params object is called. Our `ajax()` function should
    // either throw an error when an object is provided for the first argument,
    // or treat that object as the `options` argument, not `url`.

    let {
      retryAfter
    } = options;
    delete options.retryAfter; // eslint-disable-line no-param-reassign

    if (!retryAfter) {
      return makeRequest(url, options);
    }
    if (typeof retryAfter === 'number') {
      retryAfter = [retryAfter];
    }
    return new Promise((resolve, reject) => {
      let retryCount = 0;
      function retry() {
        return makeRequest(url, options).then(resolve).catch(xhr => {
          if (shouldRetry(xhr) && retryCount < retryAfter.length) {
            return later(retry, retryAfter[retryCount++]);
          } else {
            return reject(xhr);
          }
        });
      }
      return retry();
    });
  },
  jsonPost(url, data, options = {}) {
    return jsonAjax(url, 'POST', data, options);
  },
  jsonPut(url, data, options = {}) {
    return jsonAjax(url, 'PUT', data, options);
  },
  jsonGet(url, options = {}) {
    return jsonAjax(url, 'GET', null, options);
  },
  jsonDelete(url, data, options = {}) {
    return jsonAjax(url, 'DELETE', data, options);
  },
  jsonFormPut(url, formData, options = {}) {
    // eslint-disable-next-line no-param-reassign
    options = extend(true, {}, options, {
      type: 'PUT',
      dataType: 'json',
      contentType: false,
      processData: false,
      xhrFields: {
        withCredentials: true
      }
    });
    options.data = formData; // eslint-disable-line no-param-reassign
    return ajax(url, options);
  },
  uploadMultipart(url, fd, timeout = 60000) {
    return ajax(url, {
      type: 'POST',
      data: fd,
      processData: false,
      contentType: false,
      dataType: 'json',
      timeout
    });
  },
  errorMessageFromResponse(response, intl) {
    if (isUserSessionTimeout(response)) {
      return intl.t('common.messages.userSessionTimeoutError');
    } else {
      try {
        const errorMessage = getResponseMessage(response);
        if (errorMessage) {
          return escapeString(errorMessage);
        } else {
          return intl.t('common.messages.unknownError');
        }
      } catch {
        return intl.t('common.messages.unknownError');
      }
    }
  }
};

/**
 * Need to pass in Ember-intl Service
 */
export function errorMessageFromResponse(response, intl) {
  return xhrMocks.errorMessageFromResponse(response, intl);
}
export function ajax(url, ...args) {
  return xhrMocks.ajax(url, ...args);
}
function jsonAjax(url, method, data, options = {}) {
  // eslint-disable-next-line no-param-reassign
  options = extend(true, {}, options, {
    type: method,
    dataType: 'json',
    contentType: 'application/json; charset=utf-8',
    xhrFields: {
      withCredentials: true
    }
  });
  if (data) {
    options.data = JSON.stringify(data); // eslint-disable-line no-param-reassign
  }
  return ajax(url, options);
}
export function jsonPost(url, ...args) {
  return xhrMocks.jsonPost(url, ...args);
}
export function jsonPut(url, ...args) {
  return xhrMocks.jsonPut(url, ...args);
}
export function jsonGet(url, ...args) {
  return xhrMocks.jsonGet(url, ...args);
}
export function jsonDelete(url, ...args) {
  return xhrMocks.jsonDelete(url, ...args);
}
export function jsonPatch(url, ...args) {
  return jsonAjax(url, 'PATCH', ...args);
}
export function jsonFormPost(url, formData, options = {}) {
  // eslint-disable-next-line no-param-reassign
  options = extend(true, {}, options, {
    type: 'POST',
    dataType: 'json',
    contentType: false,
    processData: false,
    xhrFields: {
      withCredentials: true
    }
  });
  options.data = formData; // eslint-disable-line no-param-reassign
  return ajax(url, options);
}
export function jsonFormPut(url, formData, ...args) {
  return xhrMocks.jsonFormPut(url, formData, ...args);
}
export function uploadMultipart(url, fd, timeout) {
  return xhrMocks.uploadMultipart(url, fd, timeout);
}
export function getCsrfToken() {
  return getCookie('_js_csrf') || document.querySelector('meta[name=csrf-token]')?.getAttribute?.('content');
}